import { QuizQuestion } from '../quizEngine';

// Solana & Anchor: 100 questions (30 easy, 40 average, 30 very hard).
export const solanaAnchorQuestions: QuizQuestion[] = [
    // --- EASY (30) ---
    {
        id: 1,
        difficulty: 'easy',
        question: 'Which language are Solana on-chain programs most commonly written in?',
        choices: { a: 'Solidity', b: 'Rust', c: 'Go', d: 'TypeScript' },
        answer: 'b',
        explanation: 'Most Solana programs are written in Rust (compiled to SBF bytecode); C is also supported.',
    },
    {
        id: 2,
        difficulty: 'easy',
        question: 'Anchor is:',
        choices: {
            a: 'a framework for building Solana programs in Rust',
            b: 'a TypeScript SDK for sending Solana transactions',
            c: 'a Solana validator client written in Rust',
            d: 'a testing tool that simulates the Solana runtime',
        },
        answer: 'a',
        explanation: 'Anchor provides account validation macros, IDL generation and a client library.',
    },
    {
        id: 3,
        difficulty: 'easy',
        question: "Solana's native token is:",
        choices: { a: 'SOL', b: 'SPL', c: 'LAMP', d: 'ANCHOR' },
        answer: 'a',
        explanation: 'SOL is the native token; its smallest unit is the lamport. SPL is the token program family.',
    },
    {
        id: 4,
        difficulty: 'easy',
        question: '1 SOL equals how many lamports?',
        choices: { a: '10^3', b: '10^6', c: '10^9', d: '10^18' },
        answer: 'c',
        explanation: 'One SOL is 10^9 lamports.',
    },
    {
        id: 5,
        difficulty: 'easy',
        question: 'On Solana, program state is stored in:',
        choices: {
            a: "the program's own executable account",
            b: 'separate accounts the program owns',
            c: 'the transaction logs of past calls',
            d: 'a global key-value store per program',
        },
        answer: 'b',
        explanation: 'Programs are stateless; all persistent data lives in accounts they own.',
    },
    {
        id: 6,
        difficulty: 'easy',
        question: 'Solana programs (smart contracts) are:',
        choices: {
            a: 'stateless: they hold no data themselves',
            b: 'stateful objects with their own private storage',
            c: 'stored as source code and compiled by validators',
            d: 'unable to call other programs directly',
        },
        answer: 'a',
        explanation: 'A program is pure code; the data it operates on is passed in as accounts.',
    },
    {
        id: 7,
        difficulty: 'easy',
        question: 'A Program-Derived Address (PDA) is:',
        choices: {
            a: 'an address derived from seeds + program ID that has no private key',
            b: "a keypair whose secret key is stored inside the program's data",
            c: "an address derived from the user's public key and an incrementing nonce",
            d: 'an ed25519 key on the curve, owned by the System Program',
        },
        answer: 'a',
        explanation: 'PDAs are deterministic, off-curve addresses a program can sign for without holding a secret key.',
    },
    {
        id: 8,
        difficulty: 'easy',
        question: 'Which function derives a PDA and its bump seed?',
        choices: {
            a: 'Pubkey::create_with_seed',
            b: 'Pubkey::find_program_address',
            c: 'Pubkey::create_program_address',
            d: 'Keypair::from_seed',
        },
        answer: 'b',
        explanation: '`find_program_address` iterates bump values until it finds an off-curve address. `create_program_address` needs you to supply the bump; `create_with_seed` makes a non-PDA derived address.',
    },
    {
        id: 9,
        difficulty: 'easy',
        question: '"Rent" on Solana is:',
        choices: {
            a: 'a deposit for storing data; rent-exempt accounts persist',
            b: 'a per-epoch fee that stakers pay validators for voting',
            c: 'the priority fee paid for each compute unit a transaction uses',
            d: 'a staking reward paid to accounts that hold SOL',
        },
        answer: 'a',
        explanation: 'An account holding at least the rent-exempt minimum for its size is never charged rent.',
    },
    {
        id: 10,
        difficulty: 'easy',
        question: 'A Cross-Program Invocation (CPI) is:',
        choices: {
            a: 'one program calling another program',
            b: 'a client polling a validator for confirmations',
            c: 'validators gossiping blocks to each other',
            d: 'a transaction requesting a larger compute budget',
        },
        answer: 'a',
        explanation: 'CPIs let programs compose, e.g. calling the SPL Token program to move tokens.',
    },
    {
        id: 11,
        difficulty: 'easy',
        question: 'To sign a CPI as a PDA, you use:',
        choices: { a: 'invoke', b: 'invoke_signed', c: 'sign_with_seeds', d: 'delegate_invoke' },
        answer: 'b',
        explanation: "`invoke_signed` takes the PDA's seeds and lets the runtime treat the PDA as a signer for the inner instruction.",
    },
    {
        id: 12,
        difficulty: 'easy',
        question: "Anchor's `#[program]` module contains:",
        choices: {
            a: "the program's instruction handler functions",
            b: 'the account structs and their storage layout',
            c: 'the generated TypeScript client for the program',
            d: 'the test fixtures and local validator config',
        },
        answer: 'a',
        explanation: 'Each `pub fn` in the `#[program]` module is an instruction entrypoint.',
    },
    {
        id: 13,
        difficulty: 'easy',
        question: "Anchor's `#[derive(Accounts)]` struct describes:",
        choices: {
            a: 'the accounts an instruction expects, plus their constraints',
            b: 'the crates the program depends on, pinned to versions',
            c: 'the RPC endpoints and cluster the client connects to',
            d: 'the compute budget and priority fee for each instruction',
        },
        answer: 'a',
        explanation: 'Anchor validates each field against its constraints before your handler runs.',
    },
    {
        id: 14,
        difficulty: 'easy',
        question: 'In an Anchor handler, `Context<T>` gives access to:',
        choices: {
            a: 'the validated accounts, program ID, remaining accounts, bumps',
            b: 'the raw signed transaction bytes plus its recent blockhash',
            c: 'the current slot, epoch and cluster Unix timestamp',
            d: "the calling program's ID and any CPI return data",
        },
        answer: 'a',
        explanation: '`ctx.accounts`, `ctx.program_id`, `ctx.remaining_accounts` and `ctx.bumps`. Time comes from the Clock sysvar.',
    },
    {
        id: 15,
        difficulty: 'easy',
        question: "Anchor's `#[account]` attribute on a struct:",
        choices: {
            a: 'marks it as account data and adds an 8-byte discriminator',
            b: 'makes it a global singleton at a fixed program address',
            c: 'derives Borsh serialization but adds no type prefix',
            d: 'turns it into an event that is emitted to the logs',
        },
        answer: 'a',
        explanation: 'The discriminator identifies the account type on deserialization.',
    },
    {
        id: 16,
        difficulty: 'easy',
        question: "Anchor's 8-byte account discriminator mainly prevents:",
        choices: {
            a: 'type confusion: accepting an account of the wrong type',
            b: 'integer overflow when updating account balances',
            c: 'front-running of instructions by validators',
            d: 'rent collection on accounts below the exempt minimum',
        },
        answer: 'a',
        explanation: 'It is derived from the type name so a different type deserializes with a mismatching prefix.',
    },
    {
        id: 17,
        difficulty: 'easy',
        question: 'Which Anchor account type verifies that the account signed the transaction?',
        choices: { a: 'Signer', b: 'SystemAccount', c: 'AccountInfo', d: 'Program' },
        answer: 'a',
        explanation: '`Signer` enforces the `is_signer` flag.',
    },
    {
        id: 18,
        difficulty: 'easy',
        question: 'Which Anchor constraint creates a new account?',
        choices: { a: 'init', b: 'mut', c: 'seeds', d: 'close' },
        answer: 'a',
        explanation: '`#[account(init, payer = ..., space = ...)]` allocates and rent-funds the account.',
    },
    {
        id: 19,
        difficulty: 'easy',
        question: '`#[account(mut)]` on an account means:',
        choices: {
            a: 'the account is writable and may be modified',
            b: 'the account must be a PDA of this program',
            c: 'the account is closed at the end of the call',
            d: 'the account must have signed the transaction',
        },
        answer: 'a',
        explanation: 'Only accounts declared writable in the transaction can have their data or lamports changed.',
    },
    {
        id: 20,
        difficulty: 'easy',
        question: "Anchor's `declare_id!(...)` sets:",
        choices: {
            a: "the program's on-chain address (program ID)",
            b: "the wallet that holds the program's upgrade authority",
            c: 'the cluster URL the program is deployed to',
            d: 'the IDL version clients must use to call it',
        },
        answer: 'a',
        explanation: 'It embeds the expected program ID, which Anchor also checks at runtime.',
    },
    {
        id: 21,
        difficulty: 'easy',
        question: 'A Solana transaction is:',
        choices: {
            a: 'atomic: all instructions succeed or the whole tx fails',
            b: 'applied up to the first failing instruction, then stopped',
            c: 'limited to a single instruction per transaction',
            d: 'reversible by the fee payer within about 150 slots',
        },
        answer: 'a',
        explanation: 'Instructions execute in order and any error rolls back the entire transaction.',
    },
    {
        id: 22,
        difficulty: 'easy',
        question: 'Creating a brand-new account on Solana is done by invoking:',
        choices: { a: 'the System Program', b: 'the SPL Token program', c: 'the Config program', d: 'the Vote program' },
        answer: 'a',
        explanation: 'The System Program owns un-allocated accounts and performs allocation/assignment/transfer.',
    },
    {
        id: 23,
        difficulty: 'easy',
        question: 'Which serialization format does Anchor use by default for account data and instructions?',
        choices: { a: 'Borsh', b: 'Protobuf', c: 'RLP', d: 'JSON' },
        answer: 'a',
        explanation: 'Anchor uses Borsh (via `AnchorSerialize`/`AnchorDeserialize`).',
    },
    {
        id: 24,
        difficulty: 'easy',
        question: "Solana's consensus combines Proof of Stake with:",
        choices: { a: 'Proof of History', b: 'Proof of Work', c: 'Proof of Space', d: 'Proof of Authority' },
        answer: 'a',
        explanation: 'Proof of History is a verifiable delay clock that sequences events so validators agree on time/order.',
    },
    {
        id: 25,
        difficulty: 'easy',
        question: 'The maximum size of a Solana transaction is about:',
        choices: { a: '1,232 bytes', b: '32 KB', c: '128 KB', d: '1 MB' },
        answer: 'a',
        explanation: 'Transactions must fit in a single ~1232-byte packet (IPv6 MTU minus headers).',
    },
    {
        id: 26,
        difficulty: 'easy',
        question: 'SPL Token is:',
        choices: {
            a: 'the standard program for fungible and NFT tokens',
            b: 'the canonical bridge between Solana and Ethereum',
            c: 'the native program for staking SOL with validators',
            d: 'a wallet standard for connecting to dapps',
        },
        answer: 'a',
        explanation: 'Mints and token accounts are owned by the SPL Token program.',
    },
    {
        id: 27,
        difficulty: 'easy',
        question: 'An "associated token account" is:',
        choices: {
            a: 'a deterministic token account for a wallet + mint pair',
            b: "the account that holds the mint's freeze authority",
            c: 'a token account created fresh for every transfer',
            d: 'the escrow account a DEX uses for each trade',
        },
        answer: 'a',
        explanation: 'The Associated Token Account program derives one canonical token account (a PDA) per (owner, mint).',
    },
    {
        id: 28,
        difficulty: 'easy',
        question: 'Custom Anchor errors declared with `#[error_code]` are numbered starting at:',
        choices: { a: '0', b: '6000', c: '1', d: '65536' },
        answer: 'b',
        explanation: 'Anchor reserves lower codes for framework errors; user errors begin at 6000.',
    },
    {
        id: 29,
        difficulty: 'easy',
        question: 'Which statement about Solana programs and upgrades is TRUE?',
        choices: {
            a: 'the upgrade authority can upgrade them, or make them immutable',
            b: 'they can never be changed once deployed, like on Ethereum',
            c: 'any validator can upgrade them after a governance vote',
            d: 'upgrades require redeploying to a brand-new program ID',
        },
        answer: 'a',
        explanation: 'The upgradeable BPF loader stores an upgrade authority that can deploy new bytecode or be revoked.',
    },
    {
        id: 30,
        difficulty: 'easy',
        question: 'A Solana `Pubkey` is:',
        choices: {
            a: 'a 32-byte value, usually shown in base58',
            b: 'a 20-byte hash, usually shown in hex',
            c: 'a 33-byte compressed secp256k1 key',
            d: 'a 64-byte ed25519 signature in base64',
        },
        answer: 'a',
        explanation: 'It is a 32-byte ed25519-style public key (or an off-curve PDA of the same size).',
    },

    // --- AVERAGE (40) ---
    {
        id: 31,
        difficulty: 'medium',
        question: "Anchor's `Account<T>` type automatically checks:",
        choices: {
            a: 'that this program owns it and its discriminator matches `T`',
            b: 'that the account exists and holds the rent-exempt balance',
            c: 'that the account signed the transaction and is writable',
            d: "that the address is a PDA derived from the struct's seeds",
        },
        answer: 'a',
        explanation: 'Owner + discriminator checks are what make `Account<T>` safe against substitution.',
    },
    {
        id: 32,
        difficulty: 'medium',
        question: 'In Anchor, `UncheckedAccount` / raw `AccountInfo`:',
        choices: {
            a: 'performs no checks; you must validate it and add `/// CHECK:`',
            b: 'checks the owner automatically but skips the discriminator',
            c: 'checks the discriminator automatically but not the owning program',
            d: 'is read-only, so it can never be passed on to a CPI',
        },
        answer: 'a',
        explanation: 'Anchor forces the `/// CHECK` doc comment to acknowledge the missing safety.',
    },
    {
        id: 33,
        difficulty: 'medium',
        question: 'In `#[account(init, payer = user, space = 8 + 32 + 8)]`, the leading `8 +` accounts for:',
        choices: {
            a: 'the Anchor 8-byte discriminator',
            b: "the account's 8-byte lamport balance",
            c: 'the 8-byte PDA bump and seed length',
            d: 'the Borsh length prefix of the struct',
        },
        answer: 'a',
        explanation: "Every Anchor account needs 8 bytes for its type discriminator plus the fields' sizes. Lamports are stored outside the data.",
    },
    {
        id: 34,
        difficulty: 'medium',
        question: '`#[account(seeds = [b"vault", user.key().as_ref()], bump)]` validates that:',
        choices: {
            a: 'the address is the PDA for those seeds with the canonical bump',
            b: 'the account signed the tx using those seeds as its key',
            c: "the account's stored data begins with those exact seeds as a prefix",
            d: 'the PDA was made with any bump that yields a valid address',
        },
        answer: 'a',
        explanation: 'Anchor recomputes / re-checks the PDA so callers cannot substitute a different address.',
    },
    {
        id: 35,
        difficulty: 'medium',
        question: 'The `has_one = owner` constraint checks that:',
        choices: {
            a: "the account's `owner` field equals the `owner` account's key",
            b: 'the account is owned at runtime by the program passed as `owner`',
            c: 'the `owner` account signed the transaction as well',
            d: 'the `owner` has an associated token account for it',
        },
        answer: 'a',
        explanation: '`has_one` is sugar for `constraint = account.owner == owner.key()`; it does not check signatures.',
    },
    {
        id: 36,
        difficulty: 'medium',
        question: 'What does `#[account(close = destination)]` do at the end of the instruction?',
        choices: {
            a: 'sends its lamports to `destination` and wipes the account',
            b: 'marks the account read-only for the rest of the transaction',
            c: 'transfers ownership of the account to `destination`',
            d: 'schedules the account for deletion at the next epoch',
        },
        answer: 'a',
        explanation: "Anchor's close helper moves all lamports out and neutralizes the account (current versions reassign it to the System Program and shrink it to zero; older ones wrote a CLOSED discriminator).",
    },
    {
        id: 37,
        difficulty: 'medium',
        question: 'Why must accounts be closed carefully on Solana?',
        choices: {
            a: 'a defunded account can be refunded in the same tx and reused',
            b: 'closing an account costs a fee larger than its rent deposit',
            c: "the runtime rejects any later read of a closed account's key",
            d: 'closed accounts keep charging rent until they are purged',
        },
        answer: 'a',
        explanation: 'Accounts are only purged at the end of the transaction, so one left with data can be "revived" (reinitialization attack). Zero the data too.',
    },
    {
        id: 38,
        difficulty: 'medium',
        question: 'Solana can execute two transactions in parallel when:',
        choices: {
            a: "their writable accounts don't overlap",
            b: 'they call different programs from each other',
            c: 'they are signed by different fee payers',
            d: 'they each fit within one compute budget',
        },
        answer: 'a',
        explanation: 'Sealevel schedules non-conflicting transactions concurrently based on declared read/write locks; shared read-only accounts are fine.',
    },
    {
        id: 39,
        difficulty: 'medium',
        question: 'The default compute-unit limit for a Solana transaction is:',
        choices: {
            a: '200,000 CU per instruction, 1.4M per transaction',
            b: '30,000,000 CU per transaction, matching Ethereum gas',
            c: '48 million CU per transaction, the block limit',
            d: '21,000 CU per instruction, plus 5,000 per signature',
        },
        answer: 'a',
        explanation: 'You change it with a ComputeBudget `SetComputeUnitLimit` instruction, up to 1.4M.',
    },
    {
        id: 40,
        difficulty: 'medium',
        question: 'To request more compute units, a transaction includes:',
        choices: {
            a: 'a ComputeBudget program instruction',
            b: 'a higher `gasLimit` field in the header',
            c: 'a memo instruction naming the CU amount',
            d: 'an extra signature from the slot leader',
        },
        answer: 'a',
        explanation: '`SetComputeUnitLimit` and `SetComputeUnitPrice` are ComputeBudget instructions.',
    },
    {
        id: 41,
        difficulty: 'medium',
        question: '`invoke` vs `invoke_signed`:',
        choices: {
            a: '`invoke_signed` also passes PDA seeds so a PDA can sign',
            b: "`invoke` can't pass writable accounts; `invoke_signed` can",
            c: '`invoke_signed` works only when calling the System Program',
            d: "`invoke_signed` re-signs with the fee payer's keypair",
        },
        answer: 'a',
        explanation: 'Without seeds a PDA cannot sign; `invoke_signed` provides them for the inner call.',
    },
    {
        id: 42,
        difficulty: 'medium',
        question: 'The maximum CPI call depth on Solana is:',
        choices: { a: '4', b: '1', c: '16', d: '1024' },
        answer: 'a',
        explanation: 'A program may invoke another program up to 4 levels deep.',
    },
    {
        id: 43,
        difficulty: 'medium',
        question: 'To read the current Unix timestamp on-chain you use the:',
        choices: {
            a: 'Clock sysvar (e.g. `Clock::get()`)',
            b: 'recent blockhash of the transaction',
            c: "SlotHashes sysvar's latest entry",
            d: "Rent sysvar's `epoch` field",
        },
        answer: 'a',
        explanation: '`Clock` exposes `unix_timestamp`, `slot`, `epoch`, etc.',
    },
    {
        id: 44,
        difficulty: 'medium',
        question: "A program may modify an account's data only if:",
        choices: {
            a: 'the program owns that account',
            b: 'the account is a PDA of that program',
            c: 'the account signed the transaction',
            d: 'the account is fully rent-exempt',
        },
        answer: 'a',
        explanation: "The runtime enforces that only the owning program can mutate an account's data (and the account must be passed as writable).",
    },
    {
        id: 45,
        difficulty: 'medium',
        question: 'Which program owns a normal user wallet account?',
        choices: { a: 'the System Program', b: 'the SPL Token program', c: "the wallet's own program", d: 'the Stake program' },
        answer: 'a',
        explanation: 'Plain SOL accounts are owned by the System Program, which is why transfers go through it.',
    },
    {
        id: 46,
        difficulty: 'medium',
        question: "Anchor's `init_if_needed` is considered risky because:",
        choices: {
            a: 'it may skip init, so the handler trusts pre-created data',
            b: 'it charges rent twice when the account already exists',
            c: 'it skips writing the discriminator when the account is new',
            d: 'it cannot be combined with `seeds` on PDA accounts at all',
        },
        answer: 'a',
        explanation: 'It requires a feature flag and careful "already initialized?" guards.',
    },
    {
        id: 47,
        difficulty: 'medium',
        question: 'Integer overflow in a Solana (Rust) program built in release mode:',
        choices: {
            a: 'wraps silently unless checked math or overflow-checks is on',
            b: 'panics, since Rust checks overflow in every build profile',
            c: 'returns an `Err` that the instruction must propagate',
            d: "saturates at the type's maximum value automatically",
        },
        answer: 'a',
        explanation: 'Release builds omit overflow checks by default; use `checked_add` etc. or `overflow-checks = true` (Anchor templates enable it).',
    },
    {
        id: 48,
        difficulty: 'medium',
        question: 'The IDL that Anchor generates is used to:',
        choices: {
            a: 'describe instructions and accounts so clients can call it',
            b: "store the program's account data in a readable format",
            c: "define the program's fee schedule for each of its instructions",
            d: 'configure which validators may execute the program',
        },
        answer: 'a',
        explanation: 'The IDL is the machine-readable interface consumed by the TypeScript/Rust clients.',
    },
    {
        id: 49,
        difficulty: 'medium',
        question: '`require!(cond, MyError::X)` in Anchor:',
        choices: {
            a: 'returns the custom error if `cond` is false',
            b: 'logs the error and continues if `cond` is false',
            c: 'panics, consuming all remaining compute units',
            d: 'retries the instruction once before failing',
        },
        answer: 'a',
        explanation: '`require!`, `require_eq!`, `require_keys_eq!` are Anchor assertion macros returning errors.',
    },
    {
        id: 50,
        difficulty: 'medium',
        question: 'Zero-copy accounts (`#[account(zero_copy)]` + `AccountLoader`) are used for:',
        choices: {
            a: 'large accounts that are too big or costly to deserialize',
            b: 'accounts whose data must stay encrypted at rest on-chain',
            c: 'accounts owned by another program that you only read',
            d: 'accounts that are created once and never change',
        },
        answer: 'a',
        explanation: 'Zero-copy maps the account bytes directly instead of Borsh-deserializing into a struct on the stack/heap.',
    },
    {
        id: 51,
        difficulty: 'medium',
        question: '`Box<Account<T>>` is used to:',
        choices: {
            a: 'move account data to the heap to avoid the 4 KB stack limit',
            b: 'make the account immutable for the rest of the instruction',
            c: 'skip the discriminator check to save compute units',
            d: 'let one struct field hold several accounts at once',
        },
        answer: 'a',
        explanation: 'Large `Accounts` structs can exceed the SBF stack frame; boxing individual accounts fixes it.',
    },
    {
        id: 52,
        difficulty: 'medium',
        question: 'A "missing signer check" vulnerability is when a program:',
        choices: {
            a: 'acts for an account without checking that it signed',
            b: 'forgets to check that an account is rent-exempt',
            c: 'reads data from an account that it does not own',
            d: "lets a signer pay fees for another user's account",
        },
        answer: 'a',
        explanation: 'Authorization must be proven by a signature; using `Signer` or checking `is_signer` prevents it.',
    },
    {
        id: 53,
        difficulty: 'medium',
        question: 'An "account type confusion / cosplay" attack is prevented by:',
        choices: {
            a: 'checking the discriminator (and owner) of each account',
            b: 'raising the compute budget so every constraint check can run',
            c: 'requiring every account to sign the transaction',
            d: 'marking all accounts read-only in the transaction',
        },
        answer: 'a',
        explanation: '`Account<T>` does both the discriminator and owner check for you.',
    },
    {
        id: 54,
        difficulty: 'medium',
        question: 'An "arbitrary CPI" vulnerability is when a program:',
        choices: {
            a: 'invokes a caller-supplied program ID without checking it',
            b: 'calls back into itself recursively through a loop of CPIs',
            c: 'makes CPIs nested deeper than the maximum depth of 4',
            d: 'passes more accounts to a CPI than it received',
        },
        answer: 'a',
        explanation: 'A malicious look-alike program could report success without doing the real work.',
    },
    {
        id: 55,
        difficulty: 'medium',
        question: "Anchor's `Program<Token>` type checks:",
        choices: {
            a: 'the key equals the SPL Token program ID and it is executable',
            b: 'the token program is upgradeable only by a known, pinned authority',
            c: 'the caller holds a token account for the given mint',
            d: 'nothing; the type parameter is documentation only',
        },
        answer: 'a',
        explanation: 'Using the typed `Program` wrapper blocks the arbitrary-CPI class of bug.',
    },
    {
        id: 56,
        difficulty: 'medium',
        question: 'The rent-exempt minimum balance of an account depends on:',
        choices: {
            a: "the account's data size in bytes",
            b: 'the current SOL price in USD',
            c: 'how often the account is read',
            d: 'the size of the owning program',
        },
        answer: 'a',
        explanation: 'The rent-exempt minimum is roughly (bytes + overhead) * lamports-per-byte-year * 2 years.',
    },
    {
        id: 57,
        difficulty: 'medium',
        question: "When an account's lamport balance reaches zero:",
        choices: {
            a: 'it is purged by the runtime at the end of the transaction',
            b: 'it is frozen, but its data is kept on-chain indefinitely',
            c: 'its owner is charged a penalty from other accounts',
            d: "nothing changes until the next epoch's rent sweep",
        },
        answer: 'a',
        explanation: 'A zero-lamport account is removed; this is why "close" transfers out all lamports.',
    },
    {
        id: 58,
        difficulty: 'medium',
        question: '`system_instruction::transfer` moves:',
        choices: {
            a: 'lamports between two accounts',
            b: 'SPL tokens between token accounts',
            c: 'ownership of an account to a new program',
            d: 'program bytecode to a new buffer account',
        },
        answer: 'a',
        explanation: 'Only the System Program can debit a system-owned account, so SOL transfers use it.',
    },
    {
        id: 59,
        difficulty: 'medium',
        question: "The size of an account's data on Solana is:",
        choices: {
            a: 'fixed at creation, resizable only via `realloc`',
            b: 'unlimited, growing automatically as you write',
            c: 'always 10 MB, the maximum account size',
            d: 'set freshly by each instruction that writes it',
        },
        answer: 'a',
        explanation: 'You allocate the needed space up front; `realloc` can resize (within limits) with rent adjustments.',
    },
    {
        id: 60,
        difficulty: 'medium',
        question: 'The "duplicate mutable account" bug is:',
        choices: {
            a: 'passing one account twice where two distinct ones are assumed',
            b: 'deriving two different PDAs from the same seeds and bump',
            c: 'two programs both claiming ownership of one account',
            d: 'a Borsh layout mismatch between two versions of the same program',
        },
        answer: 'a',
        explanation: 'Mitigate with `constraint = a.key() != b.key()` (Anchor also auto-detects some cases).',
    },
    {
        id: 61,
        difficulty: 'medium',
        question: 'The `owner` field of an `AccountInfo` is:',
        choices: {
            a: 'the program that owns it and may modify its data',
            b: 'the wallet that created and funded the account',
            c: 'the fee payer of the transaction that created it',
            d: "the authority stored in the account's own data",
        },
        answer: 'a',
        explanation: 'Ownership is a runtime-enforced capability, distinct from any "authority" field in the data.',
    },
    {
        id: 62,
        difficulty: 'medium',
        question: '`#[account(address = SOME_PUBKEY)]` asserts:',
        choices: {
            a: "the account's key equals that exact pubkey",
            b: 'the account is a PDA derived from that pubkey',
            c: 'that pubkey signed the current transaction',
            d: 'the account is owned by that pubkey as a program',
        },
        answer: 'a',
        explanation: 'Useful for pinning a well-known account like a config or a specific program.',
    },
    {
        id: 63,
        difficulty: 'medium',
        question: 'In Anchor, `ctx.bumps` contains:',
        choices: {
            a: 'the bumps Anchor found for the PDA accounts in the struct',
            b: 'the compute units each account constraint consumed',
            c: 'the rent-exempt minimum for each account being created',
            d: "the 8-byte discriminators of the program's instructions",
        },
        answer: 'a',
        explanation: 'You reuse them for `invoke_signed` without recomputing `find_program_address`.',
    },
    {
        id: 64,
        difficulty: 'medium',
        question: '`msg!` in a Solana program:',
        choices: {
            a: 'writes a message to the transaction logs',
            b: 'sends a message to another program via CPI',
            c: 'emits an event that other programs can read',
            d: 'aborts the instruction with a custom message',
        },
        answer: 'a',
        explanation: 'Program logs are visible to clients but not readable by other programs.',
    },
    {
        id: 65,
        difficulty: 'medium',
        question: 'The `payer` in an Anchor `init` constraint must be:',
        choices: {
            a: 'a `mut` Signer that funds the rent-exempt balance',
            b: 'any account holding enough SOL, signer or not',
            c: 'the program itself, paying from its own lamports',
            d: "the program's upgrade authority, always",
        },
        answer: 'a',
        explanation: 'Creating the account debits the payer, so it must sign and be writable.',
    },
    {
        id: 66,
        difficulty: 'medium',
        question: "Anchor's `#[instruction(...)]` on the `Accounts` struct:",
        choices: {
            a: 'lets constraints like `seeds` use instruction arguments',
            b: 'renames the instruction in the generated IDL and client',
            c: 'adds an extra required signer to the instruction',
            d: 'sets the compute budget for that instruction',
        },
        answer: 'a',
        explanation: 'For example `seeds = [b"user", name.as_bytes()]` where `name` is an instruction arg.',
    },
    {
        id: 67,
        difficulty: 'medium',
        question: 'Instructions within one Solana transaction:',
        choices: {
            a: 'execute in order and all-or-nothing',
            b: 'execute in parallel when their accounts differ',
            c: 'commit one by one, keeping earlier successes',
            d: 'each run in a separate slot, in order',
        },
        answer: 'a',
        explanation: 'They compose by operating on the same passed accounts, but any failure reverts all. Parallelism is between transactions, not within one.',
    },
    {
        id: 68,
        difficulty: 'medium',
        question: 'A program cannot, by itself:',
        choices: {
            a: 'allocate a new account without the System Program',
            b: 'read data from an account that is passed to it',
            c: 'write log messages with the `msg!` macro',
            d: 'do checked arithmetic on 64-bit and 128-bit integers',
        },
        answer: 'a',
        explanation: 'Allocation/assignment are System Program operations, invoked via CPI.',
    },
    {
        id: 69,
        difficulty: 'medium',
        question: 'The first writable signer listed in a transaction is:',
        choices: { a: 'the fee payer', b: 'the program being called', c: 'the recent blockhash account', d: 'the upgrade authority' },
        answer: 'a',
        explanation: 'The fee payer is charged the base + priority fee, even if the transaction later fails.',
    },
    {
        id: 70,
        difficulty: 'medium',
        question: 'Address Lookup Tables (v0 transactions) exist to:',
        choices: {
            a: 'reference many accounts by index, beyond the size limit',
            b: 'cache RPC responses for frequently read accounts',
            c: 'store program IDLs on-chain so clients can fetch them',
            d: 'speed up Borsh decoding of large account data',
        },
        answer: 'a',
        explanation: 'ALTs let a transaction touch far more accounts than the ~1232-byte limit would otherwise allow.',
    },

    // --- VERY HARD (30) ---
    {
        id: 71,
        difficulty: 'hard',
        question: 'Why must security-sensitive code use the "canonical" bump for a PDA?',
        choices: {
            a: 'several bumps can be valid, giving several accounts for the same seeds',
            b: 'non-canonical bumps cost far more compute to verify on-chain',
            c: 'the runtime rejects non-canonical bumps, failing the transaction',
            d: 'a non-canonical bump can land on the curve, exposing a usable private key',
        },
        answer: 'a',
        explanation: 'If you accept any bump, one logical seed set maps to many addresses. Pin the canonical (highest) bump from `find_program_address`; every valid bump is off-curve.',
    },
    {
        id: 72,
        difficulty: 'hard',
        question: 'Why does Anchor store `bump` and re-verify with `seeds = [...], bump = stored_bump`?',
        choices: {
            a: 'to pin the canonical bump without re-running the search loop',
            b: 'so the PDA can sign CPIs later without passing any seeds',
            c: 'because the bump changes whenever the account is resized',
            d: 'so the account can be closed and recreated at the same bump',
        },
        answer: 'a',
        explanation: '`find_program_address` loops; re-deriving with a fixed bump via `create_program_address` is O(1) and still blocks substitution.',
    },
    {
        id: 73,
        difficulty: 'hard',
        question: 'The pitfall of not calling `reload()` on an Anchor account after a CPI that mutates it is:',
        choices: {
            a: 'your in-memory copy is stale and shows pre-CPI data',
            b: 'the account is closed when the handler returns',
            c: 'Anchor reloads it anyway, wasting compute units',
            d: "the CPI reverts because the account wasn't reloaded",
        },
        answer: 'a',
        explanation: 'Anchor deserializes at the start; a CPI that writes the account requires an explicit `reload()`.',
    },
    {
        id: 74,
        difficulty: 'hard',
        question: 'Solana has no classic cross-program re-entrancy problem largely because:',
        choices: {
            a: 'A -> B -> A is rejected; only direct self-recursion is allowed',
            b: 'the runtime detects any recursion and reverts the transaction',
            c: 'programs cannot call other programs, so no callbacks exist',
            d: 'each account can be used by only one instruction per slot',
        },
        answer: 'a',
        explanation: 'The runtime rejects indirect re-entrancy (a program already on the stack being called back by another), CPI depth is capped at 4, and accounts are locked for the transaction.',
    },
    {
        id: 75,
        difficulty: 'hard',
        question: 'Why is `remaining_accounts` particularly dangerous in Anchor?',
        choices: {
            a: 'Anchor does no validation on them; you must check each one',
            b: 'they are charged extra rent for being passed dynamically',
            c: 'they are always read-only, so writes silently fail',
            d: "they are not counted against the transaction's compute limit",
        },
        answer: 'a',
        explanation: 'The handler must manually check owner, signer, discriminator and key of each, exactly like an `UncheckedAccount`.',
    },
    {
        id: 76,
        difficulty: 'hard',
        question: 'Why must a program verify `token_program.key() == spl_token::ID` before a token CPI?',
        choices: {
            a: 'a fake program could report success without moving tokens',
            b: 'CPIs to unverified programs cost many more compute units',
            c: "the token program's address rotates at each epoch boundary",
            d: "the runtime blocks CPIs to programs it hasn't verified",
        },
        answer: 'a',
        explanation: "This is the arbitrary-CPI bug. Pin the callee program ID (or use Anchor's typed `Program<Token>`).",
    },
    {
        id: 77,
        difficulty: 'hard',
        question: 'A freshly `create_account`-ed account has:',
        choices: {
            a: 'all-zero data, so `Account<T>` fails but `AccountInfo` passes',
            b: 'random bytes left over from an earlier use of that memory region',
            c: 'the discriminator already written by the System Program',
            d: 'the data of whatever account last lived at that address',
        },
        answer: 'a',
        explanation: 'An all-zero Anchor discriminator is invalid, so typed deserialization fails safely. Raw `UncheckedAccount` code paths must guard against it.',
    },
    {
        id: 78,
        difficulty: 'hard',
        question: 'Any account can be *credited* lamports by anyone, but only the owner may *debit*. The runtime also enforces that:',
        choices: {
            a: "total lamports across the instruction's accounts are conserved",
            b: 'credits always require a signature from the receiving account',
            c: 'PDAs can be debited by any program that can derive their address',
            d: 'lamports only move between accounts with the same owner',
        },
        answer: 'a',
        explanation: 'Per-instruction lamport conservation is a core runtime invariant.',
    },
    {
        id: 79,
        difficulty: 'hard',
        question: 'When program A does a CPI into program B, the signer/writable privileges of the passed accounts:',
        choices: {
            a: "are inherited from A; B can't gain privileges A lacked",
            b: 'are all dropped, so B sees every account as unsigned',
            c: 'are elevated, so B may write any account it receives',
            d: 'are chosen freely by B, as long as A owns the accounts',
        },
        answer: 'a',
        explanation: "Privilege can only be passed down or narrowed, never escalated; A's PDAs can additionally sign via `invoke_signed`.",
    },
    {
        id: 80,
        difficulty: 'hard',
        question: 'The cost of `find_program_address` scales with:',
        choices: {
            a: 'the number of bumps tried before an off-curve address is found',
            b: 'the size in bytes of the account data stored at that PDA address',
            c: 'the number of accounts in the whole transaction',
            d: 'the current load on the validator executing it',
        },
        answer: 'a',
        explanation: 'It decrements the bump from 255 until it finds a valid PDA, so caching the bump saves compute.',
    },
    {
        id: 81,
        difficulty: 'hard',
        question: 'The classic `init_if_needed` + user-controlled seeds bug is:',
        choices: {
            a: 'an attacker pre-creates it, init is skipped, its data is trusted',
            b: 'the account is created twice in one tx, doubling the rent paid',
            c: 'the discriminator is never written, so any type deserializes',
            d: "the user seeds collide with the System Program's own address",
        },
        answer: 'a',
        explanation: 'Always add an explicit "is this freshly initialized?" guard when using `init_if_needed`.',
    },
    {
        id: 82,
        difficulty: 'hard',
        question: 'Securely closing an account (without Anchor helpers) requires, in the same instruction:',
        choices: {
            a: 'move out all lamports, zero the data, and mark it closed',
            b: 'zero the first 8 bytes so the discriminator no longer matches',
            c: 'move out all lamports; the runtime zeroes the data for you',
            d: 'call `realloc(0)` so the account holds no data at all',
        },
        answer: 'a',
        explanation: 'Any one step alone leaves a revival window within the same transaction or later. Reassigning to the System Program is an alternative to a CLOSED marker.',
    },
    {
        id: 83,
        difficulty: 'hard',
        question: "Beyond the discriminator, a program must ALSO check an account's `owner` because:",
        choices: {
            a: 'another program could own an account with a forged discriminator',
            b: 'the owner field decides how much rent the account pays',
            c: "Anchor's discriminator check reads the owner field itself",
            d: 'the discriminator is only 8 bytes, so accidental collisions are common',
        },
        answer: 'a',
        explanation: 'Anyone can write your discriminator bytes into an account their program owns. Discriminator + owner together prove it is really yours.',
    },
    {
        id: 84,
        difficulty: 'hard',
        question: 'The Anchor account discriminator is derived as:',
        choices: {
            a: 'first 8 bytes of `sha256("account:<StructName>")`',
            b: 'first 8 bytes of `sha256("global:<StructName>")`',
            c: 'first 8 bytes of `keccak256("<StructName>")`',
            d: 'first 8 bytes of `sha256(program_id || StructName)`',
        },
        answer: 'a',
        explanation: 'Instruction discriminators use the `"global:<name>"` prefix instead.',
    },
    {
        id: 85,
        difficulty: 'hard',
        question: "Anchor's `#[account(init, ...)]` reinitialization protection works by:",
        choices: {
            a: 'failing if the account already exists with data or an owner',
            b: 'checking a global registry of every initialized PDA',
            c: "requiring the payer to be the program's upgrade authority",
            d: 'zeroing the account first, then writing the new data over it',
        },
        answer: 'a',
        explanation: '`init` assumes a brand-new account, which is why `init_if_needed` needs an explicit opt-in and manual guards.',
    },
    {
        id: 86,
        difficulty: 'hard',
        question: 'PDA "seed collision" from concatenating variable-length seeds (e.g. `b"user" + name`) is mitigated by:',
        choices: {
            a: 'fixed-length seeds, or length-prefixing / hashing variable parts',
            b: 'using a higher bump so the colliding address lands on the curve',
            c: 'adding more seeds, whatever their individual lengths',
            d: 'switching from `find_program_address` to `create_program_address`',
        },
        answer: 'a',
        explanation: '`"user"+"bob"` and `"use"+"rbob"` would otherwise derive the same address.',
    },
    {
        id: 87,
        difficulty: 'hard',
        question: 'The Instructions sysvar lets a program:',
        choices: {
            a: 'inspect the other instructions in the same transaction',
            b: 'schedule follow-up instructions to run in a later slot',
            c: 'read instructions from other transactions in the block',
            d: 'reorder the remaining instructions in the transaction',
        },
        answer: 'a',
        explanation: 'E.g. confirm a required Ed25519/secp256k1 verify instruction ran with expected parameters. This underpins the native signature-verification pattern.',
    },
    {
        id: 88,
        difficulty: 'hard',
        question: 'Native Ed25519 / secp256k1 signature verification on Solana is done by:',
        choices: {
            a: 'a native verify instruction, confirmed via the Instructions sysvar',
            b: 'an off-chain oracle that posts attestations on-chain',
            c: 'the runtime, automatically, for any account marked signer',
            d: 'a crate like ed25519-dalek compiled directly into the program binary',
        },
        answer: 'a',
        explanation: 'A separate precompile instruction earlier in the same transaction fails the whole transaction if the signature is bad; the program then checks it ran with the expected message/pubkey. In-program verification would blow the compute budget.',
    },
    {
        id: 89,
        difficulty: 'hard',
        question: 'Durable nonce transactions replace the recent blockhash with:',
        choices: {
            a: 'a value stored in a nonce account that advances on use',
            b: "the fee payer's account nonce, incremented per tx",
            c: 'a future slot number after which the tx expires',
            d: 'a signed timestamp issued by the current slot leader',
        },
        answer: 'a',
        explanation: 'The stored blockhash is consumed and rotated by AdvanceNonce, enabling offline/long-lived signed transactions without replay.',
    },
    {
        id: 90,
        difficulty: 'hard',
        question: 'The ~10,240-byte limit on Solana applies to:',
        choices: {
            a: 'the size of an account created via CPI from a program',
            b: 'the total serialized size of a v0 transaction message',
            c: "the maximum size of a program's deployed SBF bytecode",
            d: 'the return data a program may pass back from a CPI',
        },
        answer: 'a',
        explanation: 'Larger accounts must be created by the client or grown with `realloc`; Anchor\'s `#[account(zero)]` handles the "created bigger in a prior instruction" pattern.',
    },
    {
        id: 91,
        difficulty: 'hard',
        question: "Anchor's `#[account(zero)]` constraint (vs `init`):",
        choices: {
            a: 'expects a pre-allocated account whose data is still all zeros',
            b: "zeroes an existing account's data before your handler runs at all",
            c: "sets the account's lamport balance to zero after the call",
            d: 'is an alias for `close` that keeps the rent in the account',
        },
        answer: 'a',
        explanation: 'Used for accounts too large to create via CPI: created in a previous instruction, then validated as pristine before your handler initializes it.',
    },
    {
        id: 92,
        difficulty: 'hard',
        question: 'Priority fees on Solana are:',
        choices: {
            a: 'an extra per-compute-unit price set via `SetComputeUnitPrice`',
            b: 'a flat 5,000-lamport surcharge on top of the base fee',
            c: 'a tip paid to stakers pro rata at the end of each epoch',
            d: 'burned in full, like the Ethereum base fee, to offset inflation',
        },
        answer: 'a',
        explanation: 'Total fee is roughly the base signature fee + (compute-unit limit * compute-unit price); the leader uses it to order/select transactions.',
    },
    {
        id: 93,
        difficulty: 'hard',
        question: 'Why can `Clock::get()` work without the Clock account being passed to the instruction, while some sysvars must still be passed?',
        choices: {
            a: 'a few sysvars are exposed via a syscall; others need the account',
            b: "the Clock lives in each program's data, updated every slot",
            c: 'the runtime injects every sysvar into every instruction automatically',
            d: "the Clock is cached in the transaction's recent blockhash",
        },
        answer: 'a',
        explanation: 'The `Sysvar::get()` syscall covers Clock, Rent, EpochSchedule etc.; e.g. Instructions and SlotHashes still need the account.',
    },
    {
        id: 94,
        difficulty: 'hard',
        question: 'Sealevel schedules parallel execution by having each transaction declare:',
        choices: {
            a: 'every account it touches and whether each is writable',
            b: 'a priority number chosen by the sender for ordering',
            c: 'the programs it calls, so they run on separate cores',
            d: 'nothing; it executes optimistically and rolls back conflicts',
        },
        answer: 'a',
        explanation: 'Overlapping writable accounts serialize; disjoint access sets run in parallel.',
    },
    {
        id: 95,
        difficulty: 'hard',
        question: '`has_one = authority` provides:',
        choices: {
            a: 'just `account.authority == authority.key()`; no signer check',
            b: 'that `authority` matches and also signed the transaction',
            c: "that `authority` is the PDA derived from the account's seeds",
            d: 'that `authority` owns the account at the runtime level',
        },
        answer: 'a',
        explanation: 'If you need authorization, also type `authority` as `Signer` or add a `constraint`.',
    },
    {
        id: 96,
        difficulty: 'hard',
        question: 'The runtime lets an instruction write to an account only if:',
        choices: {
            a: 'it is marked writable in the transaction message',
            b: 'the program requests write access during execution',
            c: 'the account holds more than the rent-exempt minimum',
            d: 'it is a PDA derived from the executing program',
        },
        answer: 'a',
        explanation: 'Writability is declared statically in the transaction and enforced by the runtime (on top of the ownership rule).',
    },
    {
        id: 97,
        difficulty: 'hard',
        question: "Anchor's `realloc` constraint:",
        choices: {
            a: "resizes the account's data, with a payer covering extra rent",
            b: "moves the account's data to a new, larger account address",
            c: 'can only shrink an account, refunding its excess rent',
            d: "reallocates the program's own bytecode buffer for upgrades",
        },
        answer: 'a',
        explanation: 'Growth is capped per instruction (historically 10 KB) and needs `realloc::zero` handling.',
    },
    {
        id: 98,
        difficulty: 'hard',
        question: 'Why does a `SystemProgram::create_account` CPI fail if the target PDA already holds lamports (e.g. someone pre-funded it)?',
        choices: {
            a: '`create_account` needs a lamport-free account; `init` handles this',
            b: 'PDAs can never hold lamports before being created by their program',
            c: 'pre-funding the address changes which bump seed is canonical',
            d: 'it does not fail; the extra lamports are simply refunded to the payer',
        },
        answer: 'a',
        explanation: "A griefer can block naive PDA creation by dusting the address; the workaround is `transfer` + `allocate` + `assign`, which Anchor's `init` does for you.",
    },
    {
        id: 99,
        difficulty: 'hard',
        question: 'The rent-exempt reserve of an account is:',
        choices: {
            a: 'returned in full when the account is closed',
            b: 'burned when the account is first created',
            c: 'paid out to validators at every epoch boundary',
            d: 'locked forever, even after the account closes',
        },
        answer: 'a',
        explanation: "It is a refundable deposit proportional to the account's size; rent-exempt accounts are never charged ongoing rent.",
    },
    {
        id: 100,
        difficulty: 'hard',
        question: 'Self-recursion vs re-entrancy on Solana:',
        choices: {
            a: 'a program may CPI into itself, but A -> B -> A is rejected',
            b: 'both direct self-recursion and A -> B -> A are rejected',
            c: "A -> B -> A is allowed, but a program can't CPI itself",
            d: 'both are allowed; only the depth limit of 4 applies',
        },
        answer: 'a',
        explanation: 'The runtime limits re-entrancy to direct self-recursion (capped by the CPI depth); being called back by another program while on the stack is an error.',
    },
];
